import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { walletService } from '../services/walletService';
import { PayoutSetting, WithdrawalRequest } from '../types';
import toast from 'react-hot-toast';

export const useWallet = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletService.getWallet(),
    enabled: options?.enabled !== false,
  });
};

export const useTransactions = (params?: { 
  page?: number; 
  limit?: number; 
  type?: string;
  startDate?: string;
  endDate?: string;
  timezoneOffset?: number;
}) => {
  return useQuery({
    queryKey: ['wallet', 'transactions', params],
    queryFn: () => walletService.getTransactions(params),
  });
};

export const usePayoutSettings = () => {
  return useQuery({
    queryKey: ['wallet', 'payout-settings'],
    queryFn: () => walletService.getPayoutSettings(),
  });
};

export const useAddPayoutSetting = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: Omit<PayoutSetting, 'id' | 'userId' | 'createdAt'>) =>
      walletService.addPayoutSetting(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet', 'payout-settings'] });
      toast.success('Payout setting added successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add payout setting');
    },
  });
};

export const useDeletePayoutSetting = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => walletService.deletePayoutSetting(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet', 'payout-settings'] });
      toast.success('Payout setting deleted successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete payout setting');
    },
  });
};

export const useWithdraw = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: WithdrawalRequest) => walletService.requestWithdrawal(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      queryClient.invalidateQueries({ queryKey: ['wallet', 'withdrawals'] });
      toast.success('Withdrawal request submitted successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to submit withdrawal request');
    },
  });
};

export const useWithdrawals = (params?: { 
  page?: number; 
  limit?: number; 
  status?: string;
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ['wallet', 'withdrawals', params],
    queryFn: () => walletService.getWithdrawals(params),
  });
};

export const useBanks = (countryCode: string) => {
  return useQuery({
    queryKey: ['wallet', 'banks', countryCode],
    queryFn: () => walletService.getBankList(countryCode),
    enabled: !!countryCode,
  });
};

export const useFundWallet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { amount: number; paymentProvider: string }) => walletService.fundWallet(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      // Component handles Paystack inline popup or redirect for other providers
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to initialize payment');
    },
  });
};
